import React from 'react';
import {
  ArrowRight, ChevronRight, Star,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatRelativeTime } from '@/components/leads/mockData';
import {
  estimateDealValue, computeDealScore, getNextBestAction, getStaleDays,
} from '@/components/deal/mockDealData';


function WorkQueueListItem({
  lead,
  onSelect,
  businesses,
  creatives,
  proposals,
}) {
  const isBackendLead = !!lead.fromBackend;
  const dealScore = isBackendLead
    ? {
      priorityKey: lead.needsAttention ? 'high' : lead.stale ? 'medium' : 'low',
      priorityLabel: lead.needsAttention ? 'Act soon' : lead.stale ? 'Needs follow-up' : 'Monitor',
      priorityClassName: lead.needsAttention
        ? 'border-coral/20 bg-coral/5 text-coral'
        : lead.stale
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-50 text-slate-600',
    }
    : computeDealScore(lead, businesses, creatives, proposals);
  const staleInfo = isBackendLead
    ? {
      isStale: lead.stale,
      days: lead.lastActivityAt
        ? Math.max(0, Math.round((Date.now() - new Date(lead.lastActivityAt).getTime()) / (1000 * 60 * 60 * 24)))
        : 0,
    }
    : getStaleDays(lead, businesses, creatives, proposals);
  const nextBestAction = isBackendLead
    ? { label: lead.nextActionLabel || lead.recommendedNextStepText || 'Open workspace' }
    : getNextBestAction(lead, businesses, creatives, proposals);
  const dealValue = isBackendLead ? (lead.queueStatusLabel || 'In progress') : estimateDealValue(lead);

  return (
    <button
      type="button"
      onClick={() => onSelect(lead)}
      className={`w-full rounded-xl border border-slate-200 border-l-[3px] px-4 py-4 text-left transition-all duration-200 hover:shadow-md ${
        dealScore.priorityKey === 'high' ? 'border-l-coral' : dealScore.priorityKey === 'medium' ? 'border-l-amber-400' : 'border-l-slate-300'
      } ${staleInfo.isStale ? 'bg-amber-50/30' : 'bg-card hover:bg-slate-50/70'}`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <Badge variant="outline" className={`rounded-full border text-[11px] ${dealScore.priorityClassName}`}>
            {dealScore.priorityLabel}
            {staleInfo.isStale && ` · ${staleInfo.days}d stale`}
          </Badge>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-slate-950">{lead.businessName}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                <span className="truncate">{lead.businessCategory}</span>
                {lead.businessRating != null && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                    {lead.businessRating}
                  </span>
                )}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <p className="text-sm font-semibold text-slate-950">{dealValue}</p>
              <p className="mt-1 text-[11px] text-slate-500">Est. value</p>
            </div>
          </div>

          <div className="mt-3">
            <p className="truncate text-[15px] font-semibold text-slate-900">{lead.triggerEvent}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span>{formatRelativeTime(lead.triggerDate)}</span>
              {lead.businessAddress && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="truncate">{lead.businessAddress}</span>
                </>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-slate-500">{nextBestAction.label}</span>
            <span className="inline-flex shrink-0 items-center gap-1 font-medium text-coral">
              Open
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>

        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      </div>
    </button>
  );
}

export default React.memo(WorkQueueListItem);
